import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarClock, CalendarCheck, Briefcase, ClipboardList, FileText,
  Sparkles, ChevronRight,
} from 'lucide-react';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: 12px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Countdown = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CountNumber = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 40px;
  line-height: 1;
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const CountLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const ProgressWrap = styled.div`
  margin-top: 18px;
`;

const ProgressHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 6px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgColor};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary.main};
  transition: width 0.3s ease;
`;

const MetaRow = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const StatusCard = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: box-shadow 0.15s, transform 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardMeta = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// OBF-01 / OBB-19 - "Risk: Yuksek" rozeti ve "Dikkat Edilmesi Gerekenler"
// sinyal karti BU EKRANDAN KALDIRILDI. Ikisi de sirketin ic "bu kisi ise
// baslamayabilir" degerlendirmesiydi ve degerlendirmenin OZNESINE
// gosteriliyordu: aday, portalda otururken "T-5'te Portal Aktif Degil" (hicbir
// zaman yazilmayan bir alandan uretilen, herkes icin ateslenen bir sinyal)
// okuyordu. Veri artik sunucudan da gelmiyor (MyPreboardingHomeResponse).
// IK yuzeyi: Ise Hazirlik > Surecler + Risk sinyalleri sayfalari.
export default function OverviewTab({ home, onGoTab }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const days = home.daysUntilStart;
  // OBB-05b - null = "olcecek zorunlu kalem yok"; %0 ile KARISTIRILMAZ.
  const pct = home.overallProgressPct;
  const hasPct = pct != null;

  return (
    <Grid>
      <Hero>
        <HeroTop>
          <HeroLabel>{t('myPreboarding.overview.heroTitle')}</HeroLabel>
        </HeroTop>

        <Countdown>
          {days > 0 ? (
            <>
              <CountNumber>{days}</CountNumber>
              <CountLabel>{t('myPreboarding.overview.daysUntilStart')}</CountLabel>
            </>
          ) : (
            <CountLabel>{t('myPreboarding.overview.startDayArrived')}</CountLabel>
          )}
        </Countdown>

        <ProgressWrap>
          <ProgressHead>
            <span>{t('myPreboarding.overview.progress')}</span>
            <span>{hasPct ? `%${pct}` : t('myPreboarding.overview.noItemsYet')}</span>
          </ProgressHead>
          <ProgressTrack>
            <ProgressFill $pct={hasPct ? pct : 0} />
          </ProgressTrack>
        </ProgressWrap>

        <MetaRow>
          <MetaItem>
            <CalendarCheck size={16} />
            {t('myPreboarding.overview.expectedFirstDay')}:&nbsp;<strong>{fmtDate(home.expectedFirstDay, lang)}</strong>
          </MetaItem>
          <MetaItem>
            <CalendarClock size={16} />
            {t('myPreboarding.overview.offerAcceptedAt')}:&nbsp;<strong>{fmtDate(home.offerAcceptedAt, lang)}</strong>
          </MetaItem>
          <MetaItem>
            <Briefcase size={16} />
            {t('myPreboarding.overview.source')}:&nbsp;
            <strong>{t(`myPreboarding.overview.sourceType.${home.source}`, { defaultValue: home.source })}</strong>
          </MetaItem>
        </MetaRow>
      </Hero>

      <CardsGrid>
        <StatusCard onClick={() => onGoTab('tasks')}>
          <CardIcon $bg="rgba(2,145,239,0.10)" $color="#0291EF">
            <ClipboardList size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myPreboarding.tab.tasks')}</CardTitle>
            <CardMeta>
              {t('myPreboarding.overview.mandatoryTasks', {
                done: home.mandatoryTaskCompleted,
                total: home.mandatoryTaskTotal,
              })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard onClick={() => onGoTab('documents')}>
          <CardIcon $bg="rgba(2,211,191,0.12)" $color="#01A899">
            <FileText size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myPreboarding.tab.documents')}</CardTitle>
            <CardMeta>
              {t('myPreboarding.overview.mandatoryDocuments', {
                done: home.mandatoryDocumentCompleted,
                total: home.mandatoryDocumentTotal,
              })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard onClick={() => onGoTab('welcome')}>
          <CardIcon $bg="rgba(255,193,7,0.14)" $color="#C79100">
            <Sparkles size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myPreboarding.tab.welcome')}</CardTitle>
            <CardMeta>{t('myPreboarding.overview.welcomeHint')}</CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>
      </CardsGrid>
    </Grid>
  );
}
